import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  Row,
} from 'reactstrap';
import './sass/style.scss';
import { useParams } from 'react-router-dom';
import { useCallback, useState } from 'react';
import useCallApiAndLoad from '../../hooks/useCallApiAndLoad';
import {
  deleteGalleryPhotoEndpoint,
  deleteGalleryPropertyEndpoint,
  getPropertyToEditEndpoint,
  sortPropertyGalleryEndpoint,
  syncPropertyGalleryEndpoint,
  uploadLinksPropertiesEndpoint,
  uploadPhotosEndpoint,
  uploadPropertyGalleryResourcesEndpoint,
} from './services/propertyService';
import { PropertyDetail, UploadLinksForm } from './models/propertyModel';
import LoadingCard from '../../components/LoadingCard';
import useAuth from '../../hooks/useAuth';
import AdminPage from '../../components/admin/AdminPage';
import PropertyPhotoUploader from './components/Photos/PropertyPhotoUploader';
import PropertyGalleryGrid from './components/Photos/PropertyGalleryGrid';
import useAdminEntity from '../../hooks/useAdminEntity';
import { wait } from '../../helpers/commonHelpers';
import PropertyVideoLinksForm from './PropertyVideoLinksForm';
import { useToast } from '../../components/toast/ToastProvider';

// La sincronización y la subida por archivo pueden responder 2xx distintos de 200
const isSuccess = (status: number) => status >= 200 && status < 300;

const PropertyPhotos = () => {
  const { id = '' } = useParams();
  const toast = useToast();
  const { isLoading, callEndpoint } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();
  const [errorAlert, setErrorAlert] = useState<any>(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);
  const [galleryProgress, setGalleryProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);

  const pageName = `Recursos de la propiedad`;
  const pageParentName = `Propiedad`;
  const globalAlertOptions = {
    success: false,
    warning: true,
    onConfirm: () => setErrorAlert(null),
    title: 'Error',
    text: errorAlert || '',
  };

  const getPropertyDetails = useCallback(
    () => getPropertyToEditEndpoint(id, token),
    [id, token],
  );

  const [property, reloadProperty] = useAdminEntity<PropertyDetail>(
    getPropertyDetails,
    setErrorAlert,
  );

  const deleteImage = async (imagePath: string) => {
    if (!imagePath) {
      return;
    }
    const { status } = await callEndpoint(
      deleteGalleryPhotoEndpoint(id, imagePath, token),
    );
    if (status === 200) {
      await reloadProperty();
      toast.success('Foto eliminada de la galería.');
    } else {
      toast.error('No se pudo eliminar la foto. Intenta de nuevo.');
    }
  };

  const doDeleteAllImagesFromGallery = useCallback(async () => {
    const { status } = await callEndpoint(
      deleteGalleryPropertyEndpoint(id, token),
    );
    if (status === 200) {
      await reloadProperty();
      toast.success('Se eliminaron todas las fotos de la galería.');
    } else {
      toast.error('No se pudieron eliminar las fotos. Intenta de nuevo.');
    }
  }, [id, token, callEndpoint, reloadProperty, toast]);

  const doSaveLinksProperties = async (form: UploadLinksForm) => {
    const { status } = await callEndpoint(
      uploadLinksPropertiesEndpoint(form, id, token),
    );
    if (status === 200) {
      await reloadProperty();
      toast.success('Links de video y tour 360 guardados.');
    } else {
      toast.error('No se pudieron guardar los links. Intenta de nuevo.');
    }
  };

  // Devuelven true/false para que el uploader limpie sus previews solo si salió bien
  const uploadPropertyCover = useCallback(
    async (files: Array<any>): Promise<boolean> => {
      if (files.length === 0) return false;

      const formData = new FormData();
      files.forEach((file) => formData.append('cover', file));

      setIsUploadingCover(true);
      try {
        const { status } = await callEndpoint(
          uploadPhotosEndpoint(formData, id, token),
        );
        if (status !== 200) {
          toast.error('No se pudo subir el cover. Intenta de nuevo.');
          return false;
        }
        await reloadProperty();
        toast.success('Cover actualizado.');
        return true;
      } catch (error) {
        console.error('Error uploading cover:', error);
        toast.error('No se pudo subir el cover. Intenta de nuevo.');
        return false;
      } finally {
        setIsUploadingCover(false);
      }
    },
    [id, token, callEndpoint, reloadProperty, toast],
  );

  const doSyncPropertyGallery = useCallback(async () => {
    try {
      const { status } = await callEndpoint(
        syncPropertyGalleryEndpoint(id, token),
      );
      if (!isSuccess(status)) {
        toast.error('No se pudo sincronizar la galería. Intenta de nuevo.');
        return false;
      }
      await reloadProperty();
      toast.success('Galería sincronizada.');
      return true;
    } catch (error) {
      console.error('Error syncing property gallery:', error);
      toast.error('No se pudo sincronizar la galería. Intenta de nuevo.');
      return false;
    }
  }, [id, callEndpoint, reloadProperty, token, toast]);

  const uploadPropertyGallery = useCallback(
    async (files: Array<any>): Promise<boolean> => {
      setIsUploadingGallery(true);
      setGalleryProgress({ done: 0, total: files.length });
      let failedUploads = 0;
      try {
        // Se suben en secuencia para conservar el orden
        for (let i = 0; i < files.length; i++) {
          const formData = new FormData();
          formData.append('gallery', files[i]);
          try {
            const { status } = await callEndpoint(
              uploadPropertyGalleryResourcesEndpoint(id, formData, token),
            );
            if (!isSuccess(status)) failedUploads++;
          } catch (error) {
            console.error(`Error uploading file at index ${i}:`, error);
            failedUploads++;
          }
          setGalleryProgress({ done: i + 1, total: files.length });
        }

        // El backend procesa las fotos en segundo plano: se espera antes de sincronizar
        await wait(2000);
        const synced = await doSyncPropertyGallery();
        if (failedUploads > 0) {
          toast.error(
            `No se pudieron subir ${failedUploads} de ${files.length} fotos.`,
          );
        }
        return synced && failedUploads === 0;
      } finally {
        setIsUploadingGallery(false);
        setGalleryProgress(null);
      }
    },
    [id, token, callEndpoint, doSyncPropertyGallery, toast],
  );

  const doSortPropertyGallery = useCallback(
    async (images: string[]) => {
      const { status } = await callEndpoint(
        sortPropertyGalleryEndpoint(id, images, token),
      );
      if (status === 200) {
        // el grid ya muestra el nuevo orden: no hace falta recargar
        toast.success('Orden de la galería actualizado.');
      } else {
        toast.error('No se pudo guardar el orden. Se restaura el anterior.');
        // se vuelve a pedir la galería para que el grid recupere el orden del servidor
        await reloadProperty();
      }
    },
    [id, callEndpoint, reloadProperty, token, toast],
  );

  // solo el primer load: durante subidas se conserva la pantalla para mostrar el progreso
  if (!property)
    return <LoadingCard name={pageName} parent_name={pageParentName} />;

  const coverGallery = [property.cover].filter((img) => img !== null);
  const propertyGallery = [...property.gallery].filter((img) => img !== null);

  return (
    <AdminPage
      name={pageName}
      parentName={pageParentName}
      globalAlertOptions={globalAlertOptions}
    >
      <Row>
        <Col lg="12">
          <Card>
            <CardHeader className="py-3">
              <h3 className="mb-0">Cover</h3>
              <small style={{ color: '#525f7f' }}>
                Foto principal de la propiedad.
              </small>
            </CardHeader>
            <CardBody className="py-3">
              <Row>
                <Col md="4" className="mb-3 mb-md-0">
                  {coverGallery.length > 0 &&
                  coverGallery[0] &&
                  !String(coverGallery[0]).includes('null') ? (
                    <img
                      alt="Cover actual"
                      src={coverGallery[0]}
                      className="rounded border w-100"
                      style={{ height: 180, objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="rounded border bg-secondary text-center d-flex align-items-center justify-content-center text-muted"
                      style={{ height: 180 }}
                    >
                      Sin cover
                    </div>
                  )}
                </Col>
                <Col md="8">
                  <PropertyPhotoUploader
                    label="Subir cover"
                    maxFiles={1}
                    isUploading={isUploadingCover}
                    onUpload={uploadPropertyCover}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg="12">
          <Card>
            <CardHeader className="py-3 d-flex align-items-center justify-content-between">
              <div>
                <h3 className="mb-0 d-inline-block mr-2">Galería</h3>
                <Badge color="secondary" className="text-dark">
                  {propertyGallery.length}{' '}
                  {propertyGallery.length === 1 ? 'foto' : 'fotos'}
                </Badge>
                <small className="d-block" style={{ color: '#525f7f' }}>
                  Arrastra para cambiar el orden. La primera es la que se
                  muestra primero.
                </small>
              </div>
              <div>
                <Button
                  type="button"
                  size="sm"
                  color="info"
                  outline
                  className="mr-2"
                  onClick={doSyncPropertyGallery}
                >
                  <i className="ni ni-cloud-download-95 mr-1" />
                  Sincronización manual
                </Button>
                {propertyGallery.length > 0 && (
                  <Button
                    type="button"
                    size="sm"
                    color="danger"
                    outline
                    onClick={() => setConfirmDeleteAll(true)}
                  >
                    <i className="fa-solid fa-trash-can mr-1" />
                    Eliminar todas
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardBody className="py-3">
              {propertyGallery.length > 0 ? (
                <PropertyGalleryGrid
                  images={propertyGallery}
                  onDelete={deleteImage}
                  onSort={doSortPropertyGallery}
                />
              ) : (
                <p className="text-muted">No hay fotos en la galería.</p>
              )}
              <div className="mt-3">
                <PropertyPhotoUploader
                  label="Subir fotos a la galería"
                  isUploading={isUploadingGallery}
                  progress={galleryProgress}
                  onUpload={uploadPropertyGallery}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg="12">
          <Card>
            <CardHeader className="py-3">
              <h3 className="mb-0">Video y tour 360</h3>
            </CardHeader>
            <CardBody className="py-3">
              <PropertyVideoLinksForm
                handleForm={doSaveLinksProperties}
                property={property}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal
        centered
        isOpen={confirmDeleteAll}
        toggle={() => setConfirmDeleteAll(false)}
      >
        <div className="modal-body text-center">
          <h4 className="mb-2">
            ¿Eliminar las {propertyGallery.length} fotos de la galería?
          </h4>
          <p className="text-sm mb-0" style={{ color: '#525f7f' }}>
            Esta acción no se puede deshacer.
          </p>
        </div>
        <div className="modal-footer justify-content-center">
          <Button
            color="secondary"
            outline
            onClick={() => setConfirmDeleteAll(false)}
          >
            Cancelar
          </Button>
          <Button
            color="danger"
            onClick={() => {
              setConfirmDeleteAll(false);
              doDeleteAllImagesFromGallery();
            }}
          >
            Sí, eliminar todas
          </Button>
        </div>
      </Modal>
    </AdminPage>
  );
};

export default PropertyPhotos;
